import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface BreathingPhase {
  label: string;
  duration: number; // count (beats), actual seconds = duration * 60/bpm
  action: 'inhale' | 'hold' | 'exhale';
}

export interface Exercise {
  id: string;
  category: string;
  title: string;
  description: string;
  exercise_type: string;
  phases: BreathingPhase[] | null;
  rounds: number | null;
  instructions: string[] | null;
  image_url: string | null;
  sort_order: number;
  content_id: string | null;
  intro_duration: number; // seconds before breathing animation starts
  bpm: number; // beats per minute for breathing tempo
  created_at: string;
}

export type ExerciseInsert = Omit<Exercise, 'id' | 'created_at'>;

export function useExercises() {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchExercises();

    const channel = supabase
      .channel('exercises-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'exercises' },
        fetchExercises,
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  async function fetchExercises() {
    const { data } = await supabase
      .from('exercises')
      .select('*')
      .order('sort_order');
    setExercises((data ?? []) as Exercise[]);
    setLoading(false);
  }

  async function createExercise(exercise: ExerciseInsert) {
    const { error } = await supabase.from('exercises').insert(exercise);
    if (error) throw error;
  }

  async function updateExercise(id: string, exercise: Partial<ExerciseInsert>) {
    const { error } = await supabase
      .from('exercises')
      .update(exercise)
      .eq('id', id);
    if (error) throw error;
  }

  async function deleteExercise(id: string) {
    const { error } = await supabase
      .from('exercises')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  return {
    exercises,
    loading,
    createExercise,
    updateExercise,
    deleteExercise,
  };
}
